import styled from 'styled-components';
import { Justify } from './AllieDataGrid.types';

interface AllieTableProps {
  layout: 'auto' | 'fixed';
  $stickyFirstColumn?: boolean;
}

export const StyledTable = styled('table')<AllieTableProps>`
  table-layout: ${(props: AllieTableProps) => props.layout};
  display: flex;
  flex-direction: column;
  width: 100%;
  border-top: 1px solid var(--allie-color-neutral-20);
  padding: auto 8px 8px;
  display: table;
  white-space: nowrap;
  padding-bottom: 8px;

  ${({ $stickyFirstColumn }) =>
    $stickyFirstColumn &&
    `
    & th:first-child,
    & td:first-child {
      position: sticky;
      left: 0;
      z-index: 2;
      background-color: var(--allie-color-light-main);
    }
    & th:first-child {
      z-index: 3;
      background-color: var(--allie-color-neutral-5);
    }
    & td:first-child::after {
      content: '';
      position: absolute;
      top: 0;
      right: -4px;
      bottom: 0;
      width: 4px;
      box-shadow: inset 4px 0 4px -4px rgba(0, 0, 0, 0.1);
      pointer-events: none;
    }
  `}
`;

export const StyledHead = styled('thead')`
  background-color: white;
  user-select: none;
  position: sticky;
  top: 0;
  display: contents;
`;

export const StyledHeader = styled('th')`
  border-bottom: 1px solid var(--allie-color-neutral-20);
  background-color: var(--allie-color-neutral-5);
  color: var(--allie-color-neutral-60);
  font-weight: 600;
  align-text: left;
  padding: 0 24px;
  height: 44px;
`;

export const StyledHeaderContainer = styled('div')<{
  justify?: Justify;
}>`
  display: flex;
  display-direction: row;
  flex-wrap: nowrap;
  padding: 12px auto 12px 8px;
  ${(props) => (props.justify ? `justify-content: ${props.justify}` : '')}
`;

export const StyledHeaderTitle = styled('div')`
  display: flex;
  display-direction: column;
  align-items: end;

  .allie-text-white {
    color: currentColor;
  }
`;

export const StyledSortIcon = styled('div')`
  margin: auto 4px;
  display: flex;
  display-direction: column;
  align-items: end;
`;

export const StyledRow = styled('tr')`
  border-bottom: 1px solid var(--allie-color-neutral-20);
  border-left: 1px solid var(--allie-color-neutral-20);
  border-right: 1px solid var(--allie-color-neutral-20);

  &:active {
    background-color: var(--allie-color-neutral-30));
  };
  display: table-row;
  &:last-child {
    border-bottom-width: 0;
  }

  td.has-foot:first-child > div {
    border-radius: 10px 0 0 0;
  }
`;

export const StyledCell = styled('td')<{
  justify?: 'flex-start' | 'center' | 'flex-end';
}>`
  text-align: left;
  border-bottom: 1px solid var(--allie-color-neutral-20);
  height: 72px;

  &.has-head {
    padding-top: 0;
  }

  &&& > div {
    background-color: var(--allie-color-white);
    height: 100%;
    display: flex;
    padding: 0px 24px;
    align-items: center;
    ${(props) => (props.justify ? `justify-content: ${props.justify}` : '')}
  }
`;

export const StyledCellContent = styled('div')<{
  isOdd: boolean;
}>`
  &&&& {
    background-color: ${(props: any) =>
      props.isOdd ? 'var(--allie-color-neutral-5)' : 'var(--allie-color-white)'};
  }
`;

export const StyledFoot = styled('tfoot')`
  display: contents;
`;

export const ScrollBlock = styled.div`
  --scroll-btn-y: 18px;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const ScrollViewport = styled.div`
  width: 100%;
  overflow-x: auto;
  border: 1px solid var(--allie-color-neutral-20);
  border-radius: 12px;
  scrollbar-color: var(--allie-color-neutral-40) var(--allie-color-neutral-10);

  & > table {
    border-top: none;
  }
`;

export const ScrollToggleButton = styled.button<{
  $side: 'left' | 'right';
}>`
  position: absolute;
  top: 0;
  transform: translateY(calc(var(--scroll-btn-y) - 50%));
  transition:
    transform 0.08s linear,
    opacity 0.15s ease,
    background-color 0.15s ease;
  ${({ $side }) => ($side === 'right' ? 'right: 0.75rem;' : 'left: 0.75rem;')}
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: var(--allie-color-primary-90);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: var(--allie-color-white);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  opacity: 0;
  pointer-events: none;

  ${ScrollBlock}:hover &,
  &:focus-visible {
    opacity: 1;
    pointer-events: auto;
  }

  @media (hover: none), (pointer: coarse) {
    opacity: 1;
    pointer-events: auto;
  }

  &:hover {
    background: var(--allie-color-primary-80);
  }

  &:focus-visible {
    outline: 2px solid var(--allie-color-secondary-main);
    outline-offset: 2px;
  }

  ion-icon,
  svg {
    width: 18px;
    height: 18px;
  }
`;